/**
 * YourChannelsPanel Component
 *
 * Left panel of the Collaboration section showing expandable teams with channels.
 * - Lists user's teams with expand/collapse functionality
 * - Shows channels when a team is expanded
 * - Includes loading and error states
 */

import { useState, useCallback } from 'react';
import { makeStyles, shorthands, Spinner, mergeClasses, Tooltip } from '@fluentui/react-components';
import { ChevronRight20Regular, ChevronDown20Regular, Add16Regular, ArrowClockwise16Regular } from '@fluentui/react-icons';
import { useUserTeams } from '../../../hooks/useUserTeams';
import { useTeamChannels, invalidateTeamChannelsCache } from '../../../hooks/useTeamChannels';
import { TeamsIcon } from '../../icons/TeamsIcon';
import { AddChannelModal } from './AddChannelModal';
import type { Team } from '../../../types/user';

interface YourChannelsPanelProps {
  tenantId: string;
}

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },

  headerTitle: {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'rgba(0, 0, 0, 0.45)',
  },

  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    ...shorthands.borderRadius('4px'),
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'rgba(0, 0, 0, 0.4)',
    transitionProperty: 'background-color, color',
    transitionDuration: '0.15s',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.06)',
      color: 'rgba(0, 0, 0, 0.7)',
    },

    '&:disabled': {
      cursor: 'default',
      color: 'rgba(0, 0, 0, 0.2)',
    },
  },

  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minHeight: '100px',
  },

  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('24px'),
  },

  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    ...shorthands.padding('24px'),
    color: 'rgba(0, 0, 0, 0.5)',
    fontSize: '13px',
    textAlign: 'center',
  },

  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    ...shorthands.padding('24px'),
    color: '#B91C1C',
    fontSize: '13px',
    textAlign: 'center',
  },

  teamItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    ...shorthands.padding('10px', '12px'),
    ...shorthands.borderRadius('8px'),
    cursor: 'pointer',
    transitionProperty: 'background-color',
    transitionDuration: '0.15s',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },

  teamItemExpanded: {
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
  },

  chevron: {
    color: 'rgba(0, 0, 0, 0.4)',
    flexShrink: 0,
  },

  teamIcon: {
    width: '28px',
    height: '28px',
    ...shorthands.borderRadius('6px'),
    backgroundColor: 'rgba(95, 95, 210, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#5f5fd2',
  },

  teamName: {
    flex: 1,
    fontSize: '14px',
    fontWeight: '500',
    color: 'rgba(0, 0, 0, 0.85)',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  channelsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginLeft: '38px',
    ...shorthands.padding('4px', '0', '8px', '0'),
  },

  channelItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('6px', '12px'),
    ...shorthands.borderRadius('6px'),
    fontSize: '13px',
    color: '#5f5fd2',
    cursor: 'pointer',
    textDecoration: 'none',
    transitionProperty: 'background-color',
    transitionDuration: '0.15s',

    '&:hover': {
      backgroundColor: 'rgba(95, 95, 210, 0.08)',
    },
  },

  channelIndicator: {
    width: '4px',
    height: '4px',
    ...shorthands.borderRadius('2px'),
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    flexShrink: 0,
  },

  channelName: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  channelsLoading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('8px'),
    marginLeft: '38px',
  },

  addChannelButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    ...shorthands.padding('6px', '12px'),
    ...shorthands.borderRadius('6px'),
    fontSize: '13px',
    color: 'rgba(0, 0, 0, 0.5)',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    transitionProperty: 'background-color, color',
    transitionDuration: '0.15s',

    '&:hover': {
      backgroundColor: 'rgba(95, 95, 210, 0.08)',
      color: '#5f5fd2',
    },
  },
});

interface TeamGroupProps {
  team: Team;
  tenantId: string;
  isExpanded: boolean;
  onToggle: () => void;
  onAddChannel: () => void;
}

function TeamGroup({ team, tenantId, isExpanded, onToggle, onAddChannel }: TeamGroupProps) {
  const styles = useStyles();
  const { channels, isLoading } = useTeamChannels({
    tenantId,
    teamId: team.id,
    enabled: isExpanded,
  });

  const handleAddClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onAddChannel();
  }, [onAddChannel]);

  return (
    <div>
      <div
        className={mergeClasses(
          styles.teamItem,
          isExpanded && styles.teamItemExpanded
        )}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <span className={styles.chevron} data-testid="team-chevron">
          {isExpanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
        </span>
        <span className={styles.teamIcon}>
          <TeamsIcon size={16} />
        </span>
        <span className={styles.teamName}>{team.displayName}</span>
      </div>

      {isExpanded && (
        <>
          {isLoading ? (
            <div className={styles.channelsLoading}>
              <Spinner size="tiny" />
            </div>
          ) : (
            <div className={styles.channelsList}>
              {channels.map((channel) => (
                <div key={channel.id} className={styles.channelItem}>
                  <span className={styles.channelIndicator} />
                  <span className={styles.channelName}>{channel.displayName}</span>
                </div>
              ))}
              <button
                type="button"
                className={styles.addChannelButton}
                onClick={handleAddClick}
              >
                <Add16Regular />
                <span>Add channel</span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function YourChannelsPanel({ tenantId }: YourChannelsPanelProps) {
  const styles = useStyles();
  const { teams, isLoading, error, refetch } = useUserTeams({ tenantId });
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [addChannelModalOpen, setAddChannelModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  }, [refetch]);

  const toggleTeam = useCallback((teamId: string) => {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  }, []);

  const handleAddChannel = useCallback((team: Team) => {
    setSelectedTeam(team);
    setAddChannelModalOpen(true);
  }, []);

  const handleModalOpenChange = useCallback((_event: unknown, data: { open: boolean }) => {
    setAddChannelModalOpen(data.open);
    if (!data.open) {
      setSelectedTeam(null);
    }
  }, []);

  const handleChannelCreated = useCallback(() => {
    if (selectedTeam) {
      // Invalidate the cache for this team's channels
      invalidateTeamChannelsCache(tenantId, selectedTeam.id);
      // Collapse and immediately expand to trigger refetch
      setExpandedTeams((prev) => {
        const next = new Set(prev);
        next.delete(selectedTeam.id);
        return next;
      });
      // Re-expand after a tick to trigger refetch
      setTimeout(() => {
        setExpandedTeams((prev) => {
          const next = new Set(prev);
          next.add(selectedTeam.id);
          return next;
        });
      }, 0);
    }
  }, [selectedTeam, tenantId]);

  const headerElement = (
    <div className={styles.header}>
      <span className={styles.headerTitle}>Your Channels</span>
      <Tooltip content="Refresh teams" relationship="label">
        <button
          type="button"
          className={styles.refreshButton}
          onClick={handleRefresh}
          disabled={isLoading || isRefreshing}
          aria-label="Refresh teams"
        >
          <ArrowClockwise16Regular />
        </button>
      </Tooltip>
    </div>
  );

  if (isLoading) {
    return (
      <>
        {headerElement}
        <div className={styles.loadingContainer}>
          <Spinner size="small" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        {headerElement}
        <div className={styles.errorState}>
          <span>Failed to load teams</span>
        </div>
      </>
    );
  }

  if (teams.length === 0) {
    return (
      <>
        {headerElement}
        <div className={styles.emptyState}>
          <span>No teams available</span>
        </div>
      </>
    );
  }

  return (
    <>
      {headerElement}
      <div className={styles.container}>
        {teams.map((team) => (
          <TeamGroup
            key={team.id}
            team={team}
            tenantId={tenantId}
            isExpanded={expandedTeams.has(team.id)}
            onToggle={() => toggleTeam(team.id)}
            onAddChannel={() => handleAddChannel(team)}
          />
        ))}
      </div>

      {selectedTeam && (
        <AddChannelModal
          tenantId={tenantId}
          teamId={selectedTeam.id}
          teamName={selectedTeam.displayName}
          open={addChannelModalOpen}
          onOpenChange={handleModalOpenChange}
          onSuccess={handleChannelCreated}
        />
      )}
    </>
  );
}
